import React from 'react';
import gundiNoResultsGreen from '../../images/gundi_no_results_green.png';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProviderType } from "../common/ObjectTypes";

type WebhookApiPushModalProps = {
    providerTypes: ProviderType[];
    selectedType: string | null;
    onSelectProviderType: (pt: ProviderType) => void;
    setSelectedType: (id: string | null) => void;
};

const WebhookApiPushModal: React.FC<WebhookApiPushModalProps> = ({
    providerTypes,
    selectedType,
    onSelectProviderType,
    setSelectedType,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const specialProviderTypes = Object.fromEntries(
        providerTypes
            .filter(pt => pt.value === "generic_webhooks" || pt.value === "api_push")
            .map(pt => [pt.value, pt])
    );

    const isProviderSelected = (providerValue: string): boolean => {
        return !!(specialProviderTypes[providerValue]?.id === selectedType);
    };

    const handleSelectProviderType = (providerValue: string) => {
        const provider = specialProviderTypes[providerValue];
        if (provider) {
            onSelectProviderType(provider);
            setSelectedType(provider.id);
            navigate(`/routes/new/configureprovider/${provider.value}`);
        }
    };

    return (
        <div className="grid grid-cols-[79px_1fr] gap-3 items-start">
            <img src={gundiNoResultsGreen} alt="" aria-hidden="true" className="w-[79px]" />
            <div className="flex flex-col gap-3 items-center text-center">
                <div className="flex flex-col">
                    <p className="text-2xl font-semibold text-ui-text-primary-text">{t("Not finding what you're looking for?")}</p>
                    <p className="text-ui-cta-primary text-base font-normal">{t("We offer APIs and custom webhooks")}</p>
                </div>
                {/* An inline grid of two equal columns: both buttons take the width
                    of the wider label, so "Custom Webhook" and "Connect API" are
                    the same size in every language. */}
                <div className="inline-grid grid-cols-2 gap-4">
                    <div
                        className={`flex w-full min-w-[120px] h-9 px-6 py-2 justify-center items-center gap-2 rounded bg-white hover:bg-teal-t10 cursor-pointer transition-colors duration-300 ${isProviderSelected("generic_webhooks") ? "border-2 border-brand-accent-green" : "border border-teal-t100"}`}
                        onClick={() => handleSelectProviderType("generic_webhooks")}
                        tabIndex={0}
                        role="button"
                        aria-label={t("Select Custom Webhook")}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleSelectProviderType("generic_webhooks");
                            }
                        }}
                    >
                        <span className='text-ui-cta-primary text-base font-semibold'>{t("Custom Webhook")}</span>
                    </div>
                    <div
                        className={`flex w-full min-w-[120px] h-9 px-6 py-2 justify-center items-center gap-2 rounded bg-white hover:bg-teal-t10 cursor-pointer transition-colors duration-300 ${isProviderSelected("api_push") ? "border-2 border-brand-accent-green" : "border border-teal-t100"}`}
                        onClick={() => handleSelectProviderType("api_push")}
                        tabIndex={0}
                        role="button"
                        aria-label={t("Select API Push")}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleSelectProviderType("api_push");
                            }
                        }}
                    >
                        <span className='text-ui-cta-primary text-base font-semibold'>{t("Connect API")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebhookApiPushModal;
